import { getPageNameFromPath } from '@vimeo/ui/core/F2PJoinLogin/utils';
import { Viewer } from '@vimeo/viewer';

import { ZE } from '../types/widget_types';
import { isLocalDevEnv, isCIEnv, getTemplatedVideoUrl } from './helper';

declare global {
  interface Window {
    zE?: ZE;
  }
}

async function getZendeskCredentials() {
  const response = await fetch('/help/zendesk_messenger_jwt');
  if (!response.ok) {
    throw new Error('Failed to fetch Zendesk credentials.');
  }
  return response.json();
}

/**
 * Loads the Zendesk Messenger script dynamically.
 * @param widgetKey The Zendesk widget key.
 * @returns A promise that resolves when the script has loaded.
 */
function loadZendeskScript(widgetKey: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.getElementById('ze-snippet')) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.id = 'ze-snippet';
    script.src = `https://static.zdassets.com/ekr/snippet.js?key=${widgetKey}`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Zendesk script failed to load.'));
    document.body.appendChild(script);
  });
}

/**
 * Initializes the Zendesk Messenger with the provided credentials.
 * It logs in the user, sets metadata for bot flows, and handles the close event.
 * @param token The user's JWT token for login.
 * @param messengerDetectionId The custom field ID for passing metadata.
 */
function widgetSetup(token: string, messengerDetectionId: string) {
  if (!window.zE) {
    console.error('Zendesk Messenger is not available.');
    return;
  }

  window.zE('messenger:set', 'conversationTags', ['in_product_support']);
  window.zE('messenger:set', 'conversationFields', [
    { id: messengerDetectionId, value: 'in_product_support' },
  ]);
  if (token) {
    window.zE('messenger', 'loginUser', (callback: (token: string) => void) => {
      callback(token);
    });
  }

  // this hides the default zendesk button when user minimzes after opening
  window.zE('messenger:on', 'close', () => {
    if (window.zE) {
      window.zE('messenger', 'hide');
    }
  });

  // Hide the widget by default on initialization
  window.zE('messenger', 'hide');
}

export async function initializeWidget() {
  try {
    const { token, widget_key, messenger_detection_id } = await getZendeskCredentials();
    await loadZendeskScript(widget_key);

    if (window.zE) {
      window.zE(() => {
        if (!window.zE) {
          return;
        }
        widgetSetup(token, messenger_detection_id);
      });
    }
  } catch (error) {
    console.error('Error initializing Zendesk Messenger:', error);
  }
}

export function widgetOpen(viewer: Viewer): void {
  if (!window.zE) {
    return;
  }

  const pageName = getPageNameFromPath(window.location.pathname, viewer?.user);
  const currentUrl = getTemplatedVideoUrl(window.location.pathname);

  if (isCIEnv() || isLocalDevEnv()) {
    window.zE('messenger:set', 'conversationFields', [{ id: '36670937261713', value: pageName }]);
    window.zE('messenger:set', 'conversationFields', [{ id: '36670971897489', value: currentUrl }]);
  } else {
    window.zE('messenger:set', 'conversationFields', [{ id: '41039307846929', value: pageName }]);
    window.zE('messenger:set', 'conversationFields', [{ id: '41039366484753', value: currentUrl }]);
  }
  window.zE('messenger', 'open');
  window.zE('messenger', 'show');
}
